import { useState } from 'react';
import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useErnie from 'shared/hooks/use-ernie';
import { CategoriesTypeOption, CategoriesMobileDisplayOption, RadiusOption } from 'types/graphql';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { Categories, CategoriesFormProps, Category } from './categories.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  type: CategoriesTypeOption;
  style: RadiusOption;
  mobileDisplay: CategoriesMobileDisplayOption;
  items: Categories;
};

type UseCategoriesFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: () => void;
  isDirty: boolean;
  previewData: PreviewData;
  handleToggleReorderModal: () => void;
  isReorderModalOpen: boolean;
  handleOpenSelectImageModal: (category: Category) => void;
  handleCloseSelectImageModal: () => void;
  isSelectImageModalOpen: boolean;
  categoryToEdit: Category | null;
  handleModalSave: (orderedItems: Categories) => void;
};

function useMergeDataWithFormValues(data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const { type, style, mobileDisplay, items } = formValues;

  return {
    ...data,
    categoriesV2: {
      type,
      style,
      mobileDisplay,
      items,
    },
  };
}

export function useCategoriesForm({ data }: CategoriesFormProps): UseCategoriesFormReturn {
  const {
    categoriesV2: { type, style, mobileDisplay, items },
  } = data;

  const defaultValues = {
    type,
    style,
    mobileDisplay,
    items,
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setValue,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    register,
  } = useForm<FormValues>({ defaultValues });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();

  const updateData = useUpdateWebCustomizationSettingsV2();

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    const input = {
      categories: {
        type: submittedValues.type,
        style: submittedValues.style,
        mobileDisplay: submittedValues.mobileDisplay,
        items: submittedValues.items,
      },
    };

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your category settings have been updated', 'success');
      reset({ ...submittedValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  const formValues = watch();
  // Manually track categories/items, since it's not a true form field
  register('items');
  watch('items');

  const previewData = useMergeDataWithFormValues(data, formValues);

  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [isSelectImageModalOpen, setIsSelectImageModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);

  const handleToggleReorderModal = (): void => {
    setIsReorderModalOpen(!isReorderModalOpen);
  };

  const handleOpenSelectImageModal = (category: Category): void => {
    setCategoryToEdit(category);
    setIsSelectImageModalOpen(true);
  };

  const handleCloseSelectImageModal = (): void => {
    setIsSelectImageModalOpen(false);
    setCategoryToEdit(null);
  };

  const handleModalSave = (updatedCategories: Categories): void => {
    setValue('items', updatedCategories, { shouldDirty: true });
  };

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    handleToggleReorderModal,
    isReorderModalOpen,
    handleOpenSelectImageModal,
    handleCloseSelectImageModal,
    isSelectImageModalOpen,
    categoryToEdit,
    handleModalSave,
  };
}
